import { defineStore } from 'pinia';
import { router } from '@/router';
// import { string } from 'yup';
import axios, { AxiosError } from 'axios';
import { defaultProfile, type Profile } from '@/types';
const apiBase = import.meta.env.VITE_DRF_API_URL;

interface ErrorResponse {
    non_field_errors: string[];
}

export const useAuthStore = defineStore({

    id: 'auth',
    state: () => ({
        profile: JSON.parse(localStorage.getItem('profile') || JSON.stringify(defaultProfile())) as Profile,
        token: localStorage.getItem('token') || '',
        returnUrl: '',
        errorMessage: '',
        successMessage: '',
        loading: false,
    }),
    getters: {
        isTokenExpired(): boolean {
            const expiry = localStorage.getItem('token_expires_at');
            if (!expiry) return true;
            return new Date(expiry) < new Date();
        },
        isAuthenticated(): boolean {
            return !!this.profile?.username && !this.isTokenExpired;
        }
    },

    actions: {
        async login(username: string, password: string) {
            const data = {
                username: username,
                password: password,
            }
            try {
                const url = apiBase + "/authenticate/"
                const response = await axios.post(url, data).then(request => request.data);
                this.token = response.token;
                this.profile = response.user;

                const TOKEN_LIFETIME_MINUTES = parseInt(import.meta.env.VITE_TOKEN_LIFETIME_MINUTES || '120');
                const now = new Date();
                const expiryTime = new Date(now.getTime() + TOKEN_LIFETIME_MINUTES * 60000);

                localStorage.setItem('profile', JSON.stringify(this.profile));
                localStorage.setItem('token', JSON.stringify(this.token).replace(/"/g, ''));
                localStorage.setItem('token_expires_at', expiryTime.toISOString());
                router.push(this.returnUrl || '/');
            }
            catch (error) {
                if (axios.isAxiosError(error)) {
                    let msg = handleAxiosError(error);
                    throw msg;
                }
                else {
                    // Handle other types of errors, like network errors
                    if (error instanceof Error) {
                        throw error.message;
                    }
                }
            }
        },
        async logout() {
            try {
                const url = apiBase + "/logout/"
                const headers = get_headers();
                await axios.post(url, {}, { headers }).then(request => request.data);
                localStorage.removeItem('profile');
                localStorage.removeItem('token');
                localStorage.removeItem('token_expires_at');
                this.profile = defaultProfile();
                this.token = '';
                router.push('/auth/login2');
            }
            catch (error) {
                localStorage.removeItem('profile');
                router.push('/auth/login2');
            }
        },
        async changeProfile() {
            try {
                const url = `${apiBase}/profile/update/`;
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization':
                        'Token ' + localStorage.getItem('token'),
                };

                await axios.put(url, {
                    first_name: this.profile.first_name,
                    last_name: this.profile.last_name,
                }, { headers });

                this.successMessage = 'Profile updated successfully!';
                this.errorMessage = '';
                setTimeout(() => {
                    this.successMessage = '';
                }, 3000);  // or 3000 if you want a long delay


            } catch (err: any) {
                this.errorMessage = err.response?.data?.detail || 'Failed to update profile';
                this.successMessage = '';
                setTimeout(() => {
                    this.successMessage = '';
                }, 3000);  // or 3000 if you want a long delay
            }
        },
        refreshTokenExpiry() {
            const TOKEN_LIFETIME_MINUTES = parseInt(import.meta.env.VITE_TOKEN_LIFETIME_MINUTES || '120');
            const now = new Date();
            const expiryTime = new Date(now.getTime() + TOKEN_LIFETIME_MINUTES * 60000);
            localStorage.setItem('token_expires_at', expiryTime.toISOString());
        }

    }
});

function get_headers() {
    let token = localStorage.getItem('token');
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Token ' + token,
    };
    return headers;
}

function handleAxiosError(error: AxiosError<ErrorResponse>) {
    if (error.response && error.response.data && error.response.data.non_field_errors) {
        const errorMessage = error.response.data.non_field_errors[0];
        console.error('Axios Error:', errorMessage);
        return errorMessage
    }
    else {
        // Handle other types of Axios errors or network errors.
        console.error('Axios Error:', error.message);
        return error.message
    }
}
